import { RadialButtonType } from '@data/types';
import classNames from 'classnames';
import React from 'react';

interface Props extends RadialButtonType {}

const RadialButton: React.FC<Props> = ({
  value,
  question: questionText,
  questionIndex = 0,
  answerIndex = 0,
  setSelectedAnswers = () => {},
  selectedAnswers,
  className = '',
  alternativeStyle = false,
  size = 'small',
}) => {
  // Get current results (ensure it's a flat array)
  const answerArray = selectedAnswers ? [...selectedAnswers] : [];

  // Find the relevant item in answerArray
  const existingAnswerIndex = answerArray
    .filter((item) => item !== undefined)
    .findIndex(
      (item) =>
        item.questionIndex === questionIndex && item.answerIndex === answerIndex
    );

  const selected =
    existingAnswerIndex !== -1 &&
    answerArray[existingAnswerIndex]?.answer === value;

  return (
    <div className="relative">
      {alternativeStyle && selected && (
        <span
          className={classNames(
            'rounded-full block bg-red absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            {
              'h-[35px] w-[35px]': size === 'small',
              'h-[40px] w-[40px]': size === 'large',
            }
          )}
        >
          &nbsp;
        </span>
      )}
      <button
        className={classNames(
          'rounded-full relative overflow-hidden border border-grey-dark border-solid relative flex items-center justify-center',
          className,
          {
            '!bg-red': selected,
            'h-[25px] w-[25px]': size === 'small',
            'h-[30px] w-[30px]': size === 'large',
          }
        )}
        onClick={() => {
          const newAnswer = {
            question: questionText,
            answer: value,
            questionIndex: questionIndex || 0,
            answerIndex,
          };

          if (existingAnswerIndex !== -1) {
            // Replace the existing answer
            answerArray[existingAnswerIndex] = newAnswer;
          } else {
            // Instead of directly assigning by index, push to the array or use a more precise method
            answerArray.push(newAnswer);
          }

          // Update the state with the new flat answerArray
          setSelectedAnswers([...answerArray]); // Spread to ensure immutability
        }}
      >
        <span className="sr-only">{value}</span>
        {!alternativeStyle && selected && (
          <span className="bg-white rounded-full h-[10px] w-[10px] block">
            &nbsp;
          </span>
        )}
        {alternativeStyle && selected && (
          <span className="text-white text-xs">&lt; &gt;</span>
        )}
      </button>
      {alternativeStyle && selected && (
        <span
          className={classNames(
            'absolute -top-full text-white flex justify-center items-center -translate-y-[25%]',
            {
              'h-[25px] w-[25px] left-0 right-0 ': size === 'small',
              'h-[30px] w-[100vw] left-1/2 -translate-x-[50%]':
                size === 'large',
            }
          )}
        >
          <span className="block text-[10px] lg:text-xs bg-blue rounded-tl-lg rounded-br-lg px-4">
            {value}
          </span>
        </span>
      )}
    </div>
  );
};

export default RadialButton;
